import { Alert } from "@mantine/core";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import WarningOutlined from "@mui/icons-material/WarningOutlined";
import clsx from "clsx";
import type { ReactNode } from "react";
import classes from "./CustomAlert.module.css";

type VisualStyle = "subtle" | "coloured" | "error" | "success";

const rootVariantClass: Record<VisualStyle, string> = {
  subtle: classes.rootSubtle,
  coloured: classes.rootColoured,
  error: classes.rootError,
  success: classes.rootSuccess,
};

const iconVariantClass: Record<VisualStyle, string> = {
  subtle: classes.iconSubtle,
  coloured: classes.iconColoured,
  error: classes.iconError,
  success: classes.iconSuccess,
};

const messageVariantClass: Record<VisualStyle, string> = {
  subtle: classes.messageSubtle,
  coloured: classes.messageColoured,
  error: classes.messageError,
  success: classes.messageSuccess,
};

export default function CustomAlert({
  message,
  children,
  visualStyle,
  layout,
  className,
}: {
  message?: string;
  children?: ReactNode;
  visualStyle: VisualStyle;
  layout?: "center";
  className?: string;
}) {
  const content = children !== undefined ? children : message;
  return (
    <Alert
      radius="md"
      color={
        visualStyle === "subtle"
          ? "gray"
          : visualStyle === "coloured"
          ? "darkblue"
          : visualStyle === "error"
          ? "incorrect"
          : "correct"
      }
      icon={layout === "center" ? undefined : getIcon(visualStyle)}
      variant={visualStyle === "subtle" ? "light" : "filled"}
      className={className}
      classNames={{
        root: clsx(classes.root, rootVariantClass[visualStyle]),
        icon: iconVariantClass[visualStyle],
        wrapper: classes.wrapper,
        message: clsx(
          classes.message,
          messageVariantClass[visualStyle],
          layout === "center" && classes.messageCenter
        ),
      }}
    >
      {content}
    </Alert>
  );
}

function getIcon(visualStyle: VisualStyle) {
  switch (visualStyle) {
    case "subtle":
      return <InfoOutlined style={{ width: 20, height: 20 }} />;
    case "coloured":
      return <InfoOutlined style={{ width: 20, height: 20 }} />;
    case "error":
      return <WarningOutlined style={{ width: 20, height: 20 }} />;
    case "success":
      return <CheckCircleOutlined style={{ width: 20, height: 20 }} />;
  }
}
